// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TrashFill from "@store/Library/Components/Toolkit/Icons/Trash/TrashFill";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import {
    addGiftCardToCart,
    getGiftCardsInfos,
    unassignAllGiftCards,
    unassignGiftCard,
} from "@store/Framework/Services/GiftCardService";
import { GetGiftCardsInfoModel, GiftCardModel } from "@store/Framework/Types/ApiModels";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

export interface CheckoutReviewAndSubmitGiftCardStyles {
    form?: InjectableCss;
    giftCardSelect?: SelectPresentationProps;
    removeCardsLink?: LinkPresentationProps;
    removeCardTag?: TagPresentationProps;
    giftCardTagWrapper?: TagWrapperProps;
    successHintProps?: SelectPresentationProps["hintProps"];
    failureHintProps?: SelectPresentationProps["hintProps"];
    removeHintProps?: SelectPresentationProps["hintProps"];
}

export const giftCardFormStyles: CheckoutReviewAndSubmitGiftCardStyles = {
    successHintProps: { color: "success" },
    failureHintProps: { color: "danger" },
    giftCardTagWrapper: {
        gap: pxToRem(8),
        showTopGap: true,
        showBottomGap: true,
    },
    removeCardsLink: linkGetActionLinkProps(TrashFill),
};

const styles = giftCardFormStyles;

const CheckoutReviewAndSubmitGiftCard = () => {
    const dispatch = useDispatch();
    const [giftCardList, setGiftCardList] = useState<GiftCardModel[]>([]);
    const [selectHintProps, setSelectHintProps] = useState<SelectProps>({});
    const [giftCardError, setGiftCardError] = useState<boolean>(false);

    useEffect(() => {
        getGiftCardsInfos().then((data: GetGiftCardsInfoModel) => setGiftCardList(data.giftCards));
    }, []);

    if (!giftCardList.length) {
        return null;
    }

    const onGiftCardChangeSuccess = (
        data: GetGiftCardsInfoModel,
        message: string,
        style?: TypographyPresentationProps | undefined,
    ) => {
        setGiftCardList(data.giftCards);
        dispatch(loadCurrentCart());
        setSelectHintProps({
            hint: message,
            hintProps: style ?? styles.removeHintProps,
        });
        setGiftCardError(false);
    };

    const onGiftCardChangeFailure = (message?: string) => {
        setSelectHintProps({
            hint: message ?? translate("An error has occurred. Contact support if the problem persists."),
            hintProps: styles.failureHintProps,
        });
        setGiftCardError(true);
    };

    const handleGiftCardChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const giftCardId = event.currentTarget.value;
        addGiftCardToCart(giftCardId).then(onfulfilled => {
            if (onfulfilled.successful) {
                getGiftCardsInfos().then((data: GetGiftCardsInfoModel) =>
                    onGiftCardChangeSuccess(data, translate("E-Coupon applied."), styles.successHintProps),
                );
            } else {
                onGiftCardChangeFailure(onfulfilled.errorMessage);
            }
        });
    };

    const removeGiftCardHandler = (giftCard: GiftCardModel) => {
        unassignGiftCard(giftCard.id).then(onfulfilled => {
            if (onfulfilled.successful) {
                getGiftCardsInfos().then((data: GetGiftCardsInfoModel) =>
                    onGiftCardChangeSuccess(data, translate("E-Coupon removed.")),
                );
            } else {
                onGiftCardChangeFailure(onfulfilled.errorMessage);
            }
        });
    };

    const removeAllGiftCardsHandler = () => {
        unassignAllGiftCards().then(onfulfilled => {
            if (onfulfilled.successful) {
                getGiftCardsInfos().then((data: GetGiftCardsInfoModel) =>
                    onGiftCardChangeSuccess(data, translate("All E-Coupons removed.")),
                );
            } else {
                onGiftCardChangeFailure(onfulfilled.errorMessage);
            }
        });
    };

    const appliedGiftCards = giftCardList.filter(giftCard => giftCard.giftCardApplied);

    return (
        <>
            <StyledWrapper {...styles.form}>
                <Select
                    {...styles.giftCardSelect}
                    label={translate("E-Coupon")}
                    value={translate("Add E-Coupon")}
                    onChange={handleGiftCardChange}
                    {...selectHintProps}
                    error={giftCardError}
                >
                    <option value="">{translate("Add E-Coupon")}</option>
                    {giftCardList.map(giftCard => (
                        <option key={giftCard.id} value={giftCard.id} disabled={giftCard.giftCardApplied}>
                            {giftCard.giftCardNumber}: {giftCard.applicableAmountDisplay}
                        </option>
                    ))}
                </Select>
                {appliedGiftCards.length > 0 && (
                    <>
                        <TagWrapper {...styles.giftCardTagWrapper}>
                            {appliedGiftCards.map(giftCard => (
                                <Tag
                                    {...styles.removeCardTag}
                                    key={giftCard.id}
                                    onDelete={e => {
                                        e.preventDefault();
                                        removeGiftCardHandler(giftCard);
                                    }}
                                >
                                    {translate("{0}:", giftCard.giftCardNumber)} {giftCard.applicableAmountDisplay}
                                </Tag>
                            ))}
                        </TagWrapper>
                        <Link
                            {...styles.removeCardsLink}
                            onClick={e => {
                                e.preventDefault();
                                removeAllGiftCardsHandler();
                            }}
                        >
                            {translate("Remove all E-Coupons")}
                        </Link>
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutReviewAndSubmitGiftCard,
    definition: {
        group: "Common",
        allowedContexts: ["CartPage", "CheckoutShippingPage", "CheckoutReviewAndSubmitPage", "OnePageCheckoutPage"],
        displayName: "GiftCard",
    },
};

export default widgetModule;
